import { useCallback, useMemo } from 'react';

import { BillingPlan, billingPlansDescriptions } from '@/features/billing';
import {
  PlanFeature,
  PlanLevel,
  PlanLimitedFeature,
  PlanName,
} from '@/features/common';
import { IDashboardJobOpening } from '@/features/dashboard';
import { useJobOpeningByIdQuery } from '@/features/job-opening';
import { useMeQuery } from '@/features/me';
import {
  ITrial,
  IWorkspace,
  useWorkspace,
  useWorkspaceQuery,
  useWorkspaceStatsQuery,
} from '@/features/workspace';
import {
  useJobOpeningIdOrNull,
  useWorkspaceId,
  useWorkspaceIdOrNull,
} from '@/hooks/router';

export const starterPlans: PlanLevel[] = [
  'starter',
  '2024-09-starter-1m-ats',
  '2024-09-starter-12m-ats',
];

export const premiumPlans: PlanLevel[] = [
  'premium',
  'premium-2021',
  'premium-exp-2023',
  'premium-v2-2023',
  'premium-60c-2023',
  'premium-45c-2024',
  'business',
  'business-exp-2023',
  'business-v2-2023',
  '2024-09-premium-1m-ats',
  '2024-09-premium-12m-ats',
  '2025-04-premium-1m-ats',
  '2025-04-premium-12m-ats',
  'premium-legacy',
  'business-legacy',
  'ATS-legacy-2024',
];

export const premiumPlansWithoutLegacyPremium: PlanLevel[] = [
  'premium',
  'business',
  'business-exp-2023',
  'business-v2-2023',
  'business-12m-v2-2023',
  'business-legacy',
  'ATS-legacy-2024',
  '2024-09-premium-1m-ats',
  '2024-09-premium-12m-ats',
  '2025-04-premium-1m-ats',
  '2025-04-premium-12m-ats',
];

export const paidPlans: PlanLevel[] = [
  ...starterPlans,
  ...premiumPlans,
  '2025-04-business-tier1-12m',
  '2025-04-business-tier2-12m',
  '2025-04-business-tier3-12m',
];

const requiredPlans: Record<PlanFeature, PlanLevel[]> = {
  api_access: premiumPlans,
  export_candidates: premiumPlans,
  candidate_attachments: premiumPlans,
  candidate_emails: [...starterPlans, ...premiumPlans],
  slack_integration: premiumPlans,
  zapier_integration: premiumPlans,
  job_opening_analytics: [...starterPlans, ...premiumPlans],
  disable_question_rating: premiumPlans,
  candidate_redirect_url: premiumPlansWithoutLegacyPremium,
  disable_score_chart: premiumPlans,
  suspicious_activities: [...starterPlans, ...premiumPlans],
  answer_paste: premiumPlans,
  create_custom_test: [...starterPlans, ...premiumPlans],
  customise_test: [...starterPlans, ...premiumPlans],
  homework_test: premiumPlans,
  async_interview_test: [...starterPlans, ...premiumPlans],
  candidate_history: [...starterPlans, ...premiumPlans],
  add_candidate: premiumPlans,
  pipeline_automation_old: [...starterPlans, ...premiumPlans],
  pipeline_automation: premiumPlans,
  bulk_actions: [...starterPlans, ...premiumPlansWithoutLegacyPremium],
  job_opening_user_rights: premiumPlansWithoutLegacyPremium,
  edit_job_description: [...starterPlans, ...premiumPlans],
  edit_test_landing: [...starterPlans, ...premiumPlans],
  candidate_answer_ai_features: [...starterPlans, ...premiumPlans],
  gmail_integration: [...starterPlans, ...premiumPlans],
  job_opening_repeated_application: [...starterPlans, ...premiumPlans],
  analytics: premiumPlans,
  sso: premiumPlans,
};

export const useGetPlanRequired = (
  feature: PlanFeature,
  planPeriod: 'monthly' | 'annual'
): BillingPlan | undefined => {
  return useMemo(
    () =>
      [
        ...new Set(
          requiredPlans[feature]
            .filter((planLevel) =>
              billingPlansDescriptions.plans
                .map((plan) => plan.planLevel)
                .includes(planLevel)
            )
            .sort(
              (planLevelA, planLevelB) =>
                getPlanStrenght(getPlanName(planLevelA)) -
                getPlanStrenght(getPlanName(planLevelB))
            )
            .map(
              (planLevel) =>
                billingPlansDescriptions.plans.find(
                  (plan) => plan.planLevel === planLevel
                )?.planCode[planPeriod]
            )
        ),
      ][0],
    [feature, planPeriod]
  );
};

/**
 * Maps all existing and legacy billing plans into simplified plan categories.
 *
 * Categories:
 * - 'free': includes current free plans and legacy variants
 * - 'starter': includes current starter plans and legacy equivalents
 * - 'premium': includes current premium plans
 * - 'premium-legacy', 'business-legacy': used internally to identify older plans
 * - 'ATS-legacy-2024': temporary grouping for old ATS premium plans
 * - Others (e.g., '2025-04-business-tierX-12m'): handled as distinct named tiers
 */
export const planLevelsType: { [K in BillingPlan]: PlanName } = {
  free: 'free',
  '2024-09-free-ats': 'free',

  starter: 'starter',
  'basic-12m-2024': 'starter',
  '2024-09-starter-1m-ats': 'starter',
  '2024-09-starter-12m-ats': 'starter',

  '2024-09-premium-1m-ats': 'ATS-legacy-2024',
  '2024-09-premium-12m-ats': 'ATS-legacy-2024',

  premium: 'premium',
  '2025-04-premium-1m-ats': 'premium',
  '2025-04-premium-12m-ats': 'premium',

  'premium-1m-2021': 'premium-legacy',
  'premium-12m-2021': 'premium-legacy',
  'premium-12m-exp-2023': 'premium-legacy',
  'premium-12m-60c-2023': 'premium-legacy',
  'premium-12m-45c-2024': 'premium-legacy',

  'business-1m-2021': 'business-legacy',
  'business-12m-2021': 'business-legacy',
  'business-12m-exp-2023': 'business-legacy',
  'business-1m-v2-2023': 'business-legacy',
  'business-12m-v2-2023': 'business-legacy',

  '2025-04-business-tier1-12m': '2025-04-business-tier1-12m',
  '2025-04-business-tier2-12m': '2025-04-business-tier2-12m',
  '2025-04-business-tier3-12m': '2025-04-business-tier3-12m',
};

const planLimits: Record<
  PlanLimitedFeature,
  Partial<Record<PlanLevel, number>>
> = {
  job_openings: {
    free: 1,
    starter: 3,
    premium: 6,
    '2025-04-business-tier1-12m': 12,
    '2025-04-business-tier2-12m': 18,
    '2025-04-business-tier3-12m': 50,
  },
  unlockable_candidates: {
    free: 5,
    'premium-exp-2023': 2000,
    'premium-60c-2023': 60,
    'premium-v2-2023': 300,
    'premium-45c-2024': 45,
  },
  tests_in_job_opening: {
    free: 1,
  },
};

/**
 * Returns the current subscription plan for a workspace.
 * If the workspace is in an active trial, the latest trial's plan is returned.
 * Otherwise, the actual `subscriptionPlan` is returned.
 *
 * @param workspace - The workspace object
 * @returns The active trial plan or the current subscription plan
 */
export const getCurrentSubscription = (
  workspace: IWorkspace
): PlanLevel | BillingPlan => {
  const trialPlan =
    !!workspace && workspace?.trialState === 'active'
      ? workspace.trials?.sort((a: ITrial, b: ITrial) => {
          const dateA = a.startedAt
            ? new Date(a.startedAt).getTime()
            : Infinity;
          const dateB = b.startedAt
            ? new Date(b.startedAt).getTime()
            : Infinity;

          return dateB - dateA;
        })?.[0]?.subscriptionPlanType
      : undefined;

  return trialPlan ?? workspace.subscriptionPlan;
};

/**
 * Retrieves the numeric limit for a specific feature based on the user's subscription plan.
 *
 * @param plan - The current billing plan or plan level.
 * @param feature - The feature to retrieve the usage limit for.
 * @returns The maximum allowed number for the given feature, or `Number.MAX_VALUE` if unlimited or undefined.
 */
export const getPlanLimit = (
  plan: BillingPlan | PlanLevel,
  feature: PlanLimitedFeature
): number => {
  const planLevel = planLevelsType[plan];

  return planLimits[feature][planLevel] || Number.MAX_VALUE;
};

/**
 * Returns a function that checks if the current workspace's subscription plan
 * allows access to a specific feature.
 *
 * @returns A function that takes a feature name and returns:
 *   - `true` if the feature is available in the current plan
 *   - `false` if not available
 *   - `null` if data is still loading or unavailable
 */
export const usePlanLimits = () => {
  const workspaceId = useWorkspaceIdOrNull();
  const { data: workspace, isSuccess } = useWorkspaceQuery({
    workspaceId: workspaceId ?? undefined,
  });

  return useCallback(
    (feature: PlanFeature): boolean | null => {
      if (!isSuccess || !workspace) {
        return null;
      }
      const currentPlan = planLevelsType[getCurrentSubscription(workspace)];

      return requiredPlans[feature].includes(currentPlan);
    },
    [isSuccess, workspace]
  );
};

/** Is ATS plan if matches format "year-month-[plan name and other details]" */
export const isATSPlan = (plan: BillingPlan | PlanLevel): boolean =>
  /^[0-9]{4}-[0-9]{2}-.+$/.test(plan);

export const isATSLegacyPlan = (plan: BillingPlan | PlanLevel): boolean =>
  /^2024.*premium/.test(plan);

export const getPlanName = (plan: BillingPlan | PlanLevel): PlanName => {
  return isATSPlan(plan)
    ? (plan.split('-')[2] as PlanName)
    : (plan.split('-')[0] as PlanName);
};

export const getPlanStrenght = (planName: PlanName): number => {
  switch (planName) {
    case 'free':
      return 0;
    case 'starter':
      return 1;
    case 'premium':
      return 2;
    case 'business':
    case 'ATS-legacy-2024':
    case 'premium-legacy':
    case 'business-legacy':
      return 3;
    case '2025-04-business-tier1-12m':
    case '2025-04-business-tier2-12m':
    case '2025-04-business-tier3-12m':
      return 4;
  }
};

export const getPlanPeriod = (plan: BillingPlan): 'monthly' | 'annual' => {
  if (/1-month|1m/.test(plan)) {
    return 'monthly';
  } else {
    return 'annual';
  }
};

export const isPaidPlan = (plan: BillingPlan): boolean => {
  return paidPlans.includes(planLevelsType[plan]);
};

/**
 * Checks whether the current workspace has reached the job opening limit
 * based on the user's subscription plan.
 *
 * @returns `true` if under the limit, `false` if the limit is reached, or `null` if loading or data is unavailable.
 */
export const useJobOpeningsLimit = (): boolean | null => {
  const workspaceId = useWorkspaceId();
  const { data: meQuery, isSuccess: isSuccessMe } = useMeQuery();
  const { data: workspaceStats, isSuccess: isSuccessStats } =
    useWorkspaceStatsQuery({ workspaceId });

  return useMemo(() => {
    if (!isSuccessMe || !isSuccessStats) {
      return null;
    }

    const workspace = meQuery.workspaces?.find(
      (workspace) => workspace.id === workspaceId
    )!;

    const planLimit = getPlanLimit(
      getCurrentSubscription(workspace),
      'job_openings'
    );

    return workspaceStats.activeJobOpenings < planLimit;
  }, [workspaceId, meQuery, workspaceStats, isSuccessMe, isSuccessStats]);
};

/**
 * Determines whether candidates should be locked based on the workspace's age
 * and current subscription plan.
 *
 * @returns `true` if the workspace is on a free plan and was created over 30 days ago.
 */
export const useLockedCandidates = (): boolean => {
  const workspace = useWorkspace();

  if (!workspace?.createdAt || !workspace.subscriptionPlan) {
    return false;
  }

  const createdDate = new Date(workspace.createdAt);
  const lockDate = new Date(createdDate);
  lockDate.setDate(createdDate.getDate() + 30);

  const isFreePlan = planLevelsType[workspace.subscriptionPlan] === 'free';
  const isOlderThan30Days = lockDate <= new Date();

  return isFreePlan && isOlderThan30Days;
};

/**
 * Hook that returns a function to check if the limit of tests
 * in a job opening has been reached, based on the user's subscription plan.
 *
 * @returns A function that returns `true` if adding more tests is allowed.
 */
export const useTestJobOpeningLimit = () => {
  const jobOpeningId = useJobOpeningIdOrNull();
  const workspace = useWorkspace();
  const { data: jobOpening } = useJobOpeningByIdQuery({
    jobOpeningId: jobOpeningId ?? undefined,
  });

  return useCallback(
    (dashboardJobOpening?: IDashboardJobOpening) => {
      const planLimit = getPlanLimit(
        getCurrentSubscription(workspace!),
        'tests_in_job_opening'
      );

      if (dashboardJobOpening?.pipelineStats?.length) {
        const assignedTestsCount = dashboardJobOpening.pipelineStats.filter(
          (stats) => Boolean(stats.testId)
        ).length;

        return assignedTestsCount < planLimit;
      }

      if (jobOpening?.pipelineStages?.length) {
        const assignedTestsCount = jobOpening.pipelineStages.filter((stage) =>
          Boolean(stage.testId)
        ).length;

        return assignedTestsCount < planLimit;
      }

      return true;
    },
    [jobOpening, workspace]
  );
};
